import './autopatchStyles.css';
import _ from 'lodash';

/**
 * Autopatch directive
 *
 * Minimal examples:
 * ```
 * <div autopatch="{ model:'post', id:vm.post.id }">
 * <div autopatch="{ model:'comment', endpoint: '/posts/'+vm.post.id+'/comments/'+vm.comment.id }">
 * <div autopatch="[
 *   { model:'story', id:vm.story.id },
 *   { model:'post',  id:vm.post.id }
 * ]">
 * <div autopatch="autopatchOptions">
 * ```
 * Available options:
 * model  The name to look for in the ng-model attribute. E.g. "post" in "vm.post.headline".
 * [id]  The record id for simple endpoints. E.g. if `model` is "post" and `id` is 123, it will call PATCH /posts/123
 * [endpoint]  If `id` is not given, `endpoint` MUST be given. The URL to which to patch. E.g. "/posts/123/comments/456"
 * [autopatchTransitionMs=1000]  The number of milliseconds to leave the "autopatch-ok" css class
 * [onBeforePatch]  Called before PATCH request with a DOM `change` event
 * [onAfterPatch]  Called after PATCH request with a DOM `change` event
 * [onAfterError]  Called when POST or PATCH returns an error with a DOM `change` event
 *
 * The event object for these events has additional properties:
 *    target  The input that fired the change event
 *    field  The field that will be patched. e.g. "headline" for ng-model="vm.post.headline"
 *    value  The new value that the field has
 *    url    The full URL that will be used
 *    payload  The object to be PATCHed with the `id` value and field value. e.g. {headline:"foo"}. Alter the payload to change what is PATCHed.
 *    ngModelAttr  The full string value of the ng-model attribute. e.g. "vm.post.headline"
 *    preventDefault()  Call this to cancel the PATCH (only for onBeforePatch)
 *    response  The raw XHR response (only for onAfter* events)
 */
export default function autopatchDirective($http) {
	'ngInject';
	return {
		restrict: 'A',
		scope: {
			options: '=autopatch',
		},
		link: autopatchLink,
	};
	//
	// functions we use
	//
	function autopatchLink($scope, $elem, attr) {
		$elem.on('change', handleChange);

		//
		// functions we use
		//
		function handleChange(evt) {
			var target = angular.element(evt.target);
			// does this input (or one of its parents) have an ng-model attribute?
			var ngModelAttr = getNgModelAttribute(evt.target);
			attr.debug && console.log('detected change! ngModelAttr=' + ngModelAttr);
			if (!ngModelAttr) {
				// nope, nothing to do
				return;
			}
			// check the ng-model attribute value against all the options sets
			(_.isArray($scope.options) ? $scope.options : [$scope.options]).forEach(patchIfMatch);

			// if this ngModel matches the given attribute set run http patch
			function patchIfMatch(options) {
				if (!options.model) {
					throw new Error('autopatch directive: `model` is a required option.');
				}
				var regex = new RegExp('^(vm.)?' + options.model + '.');
				// FYI, sometimes autopatch prohibits other event handlers from firing. autopatch-ignore doesn't change that.
				if (ngModelAttr.match(regex) && !target.hasClass('autopatch-ignore')) {
					setTimeout(function () {
						// set timeout to give auto formatters a chance to work
						patchit(evt, ngModelAttr, options);
					}, 0);
				}
			}
		}

		// simple way to make an endpoint plural
		// we don't have any irregular endpoint words at the moment
		function pluralize(word) {
			word = word.replace(/y$/, 'ie');
			word += 's';
			return word;
		}

		// get the ng-model attribute of this input
		// or traverse up the dom tree to try to find one
		function getNgModelAttribute(node) {
			var attr;
			while (node && node !== $elem[0]) {
				attr = node.getAttribute('ng-model');
				if (attr) {
					break;
				}
				node = node.parentNode;
			}
			return attr;
		}

		// actually run the http patch
		function patchit(evt, ngModelAttr, options) {
			var input = angular.element(evt.target);
			input.addClass('autopatch');
			var field = ngModelAttr.replace(/^(.+)\.([^\.]+)$/, '$2');
			var value = angular.element(input).val();
			if (evt.target.tagName.toUpperCase() === 'SELECT') {
				// wtf angular your .val() function returns weirdness on select elements sometimes
				value = value.replace(/^\s?string:(.+)\s?$/, '$1');
			}
			// get endpoint and values
			var endpoint;
			if (options.endpoint) {
				endpoint = options.endpoint;
			} else if (options.id) {
				endpoint = '/' + pluralize(options.model) + '/' + options.id;
			} else {
				throw new Error('autopatch directive: `endpoint` or `id` is required.');
			}
			var url = '/api/v2' + endpoint;
			var payload = {};
			payload[field] = value;
			// allow callback
			fire('onBeforePatch', evt, { field, value, url, payload, ngModelAttr });
			if (evt.defaultPrevented) {
				return;
			}
			attr.debug && console.log('patching...', evt.url, evt.payload);
			// run patch
			$http.patch(evt.url, evt.payload).then(function (response) {
				if (response.status < 200 || response >= 300) {
					input.addClass('autopatch-error');
					fire('onAfterError', evt, { response: response });
					return;
				}
				// after events
				fire('onAfterPatch', evt, { response: response });
			});

			input.addClass('autopatch-ok');
			input.removeClass('autopatch-error');
			setTimeout(function () {
				input.removeClass('autopatch-ok');
			}, options.autopatchTransitionMs || 1000);

			// fire callbacks defined in options
			function fire(type, event, extend) {
				_.extend(event, extend);
				if (!_.isFunction(event.preventDefault)) {
					event.preventDefault = function () {
						event.defaultPrevented = true;
					};
				}
				if (_.isFunction(options[type])) {
					options[type].call(options, event);
				}
			}
		}
	}
}
